import { useEffect, useState } from 'react'
import {
  CustomCell,
  CustomRenderer,
  drawTextCell,
  GridCellKind,
  ProvideEditorCallback,
  Theme,
} from '@glideapps/glide-data-grid'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { DateTimeColOptionsOnly } from '@makyoapp/makyo-common'

import { IDateTimeCell } from '@/core/models/columns/dateTime'
import { debounce } from '@/shared/utils'

export interface CustomDateTimeCellProps {
  readonly type: ColumnTypes.DateTime
  readonly cell: IDateTimeCell
  readonly settings: DateTimeColOptionsOnly
  readonly locale: string
  readonly extra: {
    readonly theme: string
  }
}

export type CustomDateTimeCell = CustomCell<CustomDateTimeCellProps>

interface TextEditorProps {
  value?: string
  onChange: (nextValue?: string) => void
  themeMode?: string
  theme?: Theme
}

const TextEditor = ({ value, onChange, themeMode, theme }: TextEditorProps) => {
  const [activeValue, setActiveValue] = useState(value || '')

  useEffect(() => {
    onChange(activeValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeValue])

  return (
    <input
      className='w-full pt-1'
      type='text'
      value={activeValue}
      onChange={(e) => {
        setActiveValue(e.target.value)
      }}
      autoFocus
      style={{
        color: themeMode === 'dark' ? theme?.textDark : theme?.textDark,
        backgroundColor: theme?.bgCell,
      }}
    />
  )
}

const Editor: ReturnType<ProvideEditorCallback<CustomDateTimeCell>> = (args) => {
  const cell = args.value.data.cell
  const cellValue = cell.toEditorString()
  const themeMode = args.value.data.extra.theme

  const handleChange = (textValue?: string) => {
    if (!textValue) return

    const updatedCell = cell.toCell(textValue) as IDateTimeCell

    args.onChange({
      ...args.value,
      data: {
        ...args.value.data,
        cell: updatedCell,
      },
      copyData: updatedCell.toString(),
    } as CustomDateTimeCell)
  }

  const debouncedHandleChange = debounce(handleChange, 150)

  return <TextEditor value={cellValue} themeMode={themeMode} theme={args.theme} onChange={debouncedHandleChange} />
}

const CustomDateTimeRenderer: CustomRenderer<CustomDateTimeCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is CustomDateTimeCell =>
    (cell.data as CustomDateTimeCellProps).type === ColumnTypes.DateTime,
  draw: (args, cell) => {
    const stringValue = cell.data.cell.toString()
    drawTextCell(args, stringValue, cell.contentAlign)
  },
  provideEditor: () => ({
    editor: Editor,
  }),
  onPaste(val, cellData) {
    return {
      ...cellData,
      cell: cellData.cell.toCell(val) as IDateTimeCell,
    }
  },
  onDelete(cellData) {
    return {
      ...cellData,
      data: {
        ...cellData.data,
        cell: cellData.data.cell.toCell('') as IDateTimeCell,
      },
    }
  },
}

export default CustomDateTimeRenderer
